import { useState, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import './AuthModal.css';

/**
 * Multi-step auth modal. Keeps the original prop interface so LandingPage
 * never has to change. Internally drives a small state machine:
 *
 *   signup:  email -> otp -> setup(password+profile) -> onSuccess
 *   signin:  signin(email+password)  (+ forgot -> reset email)
 */
export default function AuthModal({ mode, initialEmail, onClose, onSuccess, onSwitchMode }) {
  const [step, setStep] = useState(mode === 'signin' ? 'signin' : 'email');
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const otpRef = useRef(null);

  const guardConfig = () => {
    if (!isSupabaseConfigured) {
      setError('Authentication is not configured yet. Add your Supabase keys to .env.local and restart.');
      return false;
    }
    return true;
  };

  const reset = (s) => { setError(''); setInfo(''); setStep(s); };

  // ── Step 1: send OTP to email (signup) ──
  const sendOtp = async (e) => {
    e.preventDefault();
    if (!guardConfig()) return;
    setBusy(true); setError(''); setInfo('');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    setBusy(false);
    if (error) return setError(error.message);
    setInfo(`We sent a 6-digit code to ${email}`);
    setStep('otp');
    setTimeout(() => otpRef.current?.focus(), 100);
  };

  // ── Step 2: verify the OTP ──
  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!guardConfig()) return;
    setBusy(true); setError('');
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: otp.trim(),
      type: 'email',
    });
    setBusy(false);
    if (error) return setError(error.message);

    // If this account already finished setup, log straight in.
    if (data.user?.user_metadata?.profile_complete) {
      onSuccess?.();
      onClose?.();
    } else {
      reset('setup');
    }
  };

  // ── Step 3: set password + basic profile for new users ──
  const completeSetup = async (e) => {
    e.preventDefault();
    if (!guardConfig()) return;
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true); setError('');
    const { error } = await supabase.auth.updateUser({
      password,
      data: { name, company, profile_complete: true },
    });
    setBusy(false);
    if (error) return setError(error.message);
    onSuccess?.();
    onClose?.();
  };

  // ── Sign in with password ──
  const signIn = async (e) => {
    e.preventDefault();
    if (!guardConfig()) return;
    setBusy(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) return setError(error.message);
    onSuccess?.();
    onClose?.();
  };

  // ── Forgot password: send reset link ──
  const sendReset = async (e) => {
    e.preventDefault();
    if (!guardConfig()) return;
    setBusy(true); setError('');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    setBusy(false);
    if (error) return setError(error.message);
    setStep('forgot-sent');
  };

  const resendOtp = async () => {
    if (!guardConfig()) return;
    setBusy(true); setError(''); setInfo('');
    const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
    setBusy(false);
    if (error) return setError(error.message);
    setInfo('A new code is on its way.');
  };

  // ── Headings per step ──
  const titles = {
    email:        'Create Your Account',
    otp:          'Verify Your Email',
    setup:        'Set Up Your Account',
    signin:       'Sign In',
    forgot:       'Reset Password',
    'forgot-sent':'Check Your Email',
  };

  return (
    <div className="auth-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose}>✕</button>

        {/* Step indicator for the signup journey */}
        {['email', 'otp', 'setup'].includes(step) && (
          <div className="auth-steps">
            <span className={`auth-step-dot ${step === 'email' ? 'active' : ''} ${['otp','setup'].includes(step) ? 'done' : ''}`} />
            <span className={`auth-step-dot ${step === 'otp' ? 'active' : ''} ${step === 'setup' ? 'done' : ''}`} />
            <span className={`auth-step-dot ${step === 'setup' ? 'active' : ''}`} />
          </div>
        )}

        <h2>{titles[step]}</h2>

        {error && <div className="auth-error">{error}</div>}
        {info && !error && <div className="auth-info">{info}</div>}

        {/* ── EMAIL ── */}
        {step === 'email' && (
          <form onSubmit={sendOtp} className="auth-form">
            <p className="auth-lead">Enter your email and we'll send you a verification code.</p>
            <input type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)} required autoFocus />
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send Verification Code'}
            </button>
            <p className="auth-switch">
              Already have an account? <span onClick={() => reset('signin')}>Sign in.</span>
            </p>
          </form>
        )}

        {/* ── OTP ── */}
        {step === 'otp' && (
          <form onSubmit={verifyOtp} className="auth-form">
            <p className="auth-lead">Enter the verification code sent to <strong>{email}</strong>.</p>
            <input ref={otpRef} type="text" inputMode="numeric" maxLength={8}
              placeholder="Enter code" value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              className="auth-otp-input" required />
            <button type="submit" className="auth-submit" disabled={busy || otp.length < 6}>
              {busy ? 'Verifying…' : 'Verify & Continue'}
            </button>
            <p className="auth-switch">
              Didn't get it? <span onClick={resendOtp}>Resend code.</span>
              {'  ·  '}
              <span onClick={() => reset('email')}>Change email.</span>
            </p>
          </form>
        )}

        {/* ── SETUP ── */}
        {step === 'setup' && (
          <form onSubmit={completeSetup} className="auth-form">
            <p className="auth-lead">You're verified! Finish setting up your account.</p>
            <input type="text" placeholder="Full name" value={name}
              onChange={e => setName(e.target.value)} required autoFocus />
            <input type="text" placeholder="Company name" value={company}
              onChange={e => setCompany(e.target.value)} />
            <input type="password" placeholder="Create a password (min 6 chars)" value={password}
              onChange={e => setPassword(e.target.value)} required />
            <input type="password" placeholder="Confirm password" value={confirm}
              onChange={e => setConfirm(e.target.value)} required />
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create Account & Continue'}
            </button>
          </form>
        )}

        {/* ── SIGN IN ── */}
        {step === 'signin' && (
          <form onSubmit={signIn} className="auth-form">
            <input type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)} required autoFocus />
            <input type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} required />
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
            <p className="auth-switch">
              <span onClick={() => reset('forgot')}>Forgot password?</span>
            </p>
            <p className="auth-switch">
              New to Axion? <span onClick={() => reset('email')}>Sign up now.</span>
            </p>
          </form>
        )}

        {/* ── FORGOT ── */}
        {step === 'forgot' && (
          <form onSubmit={sendReset} className="auth-form">
            <p className="auth-lead">Enter your email and we'll send a password reset link.</p>
            <input type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)} required autoFocus />
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send Reset Link'}
            </button>
            <p className="auth-switch">
              <span onClick={() => reset('signin')}>← Back to sign in</span>
            </p>
          </form>
        )}

        {/* ── FORGOT SENT ── */}
        {step === 'forgot-sent' && (
          <div className="auth-form">
            <p className="auth-lead">
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
              Click it to set a new password.
            </p>
            <button className="auth-submit" onClick={onClose}>Got it</button>
          </div>
        )}
      </div>
    </div>
  );
}
